import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WordMode, Category } from '../../types/tableGame';
import InfoButton from './InfoButton';
import CategorySelector from '../CategorySelector';

interface WordModeSelectorProps {
  wordMode: WordMode;
  customWord?: string;
  categoryId?: number;
  categories: Category[];
  onModeChange: (mode: WordMode) => void;
  onCustomWordChange: (word: string) => void;
  onCategoryChange: (categoryId: number) => void;
  disabled?: boolean;
}

const WordModeSelector: React.FC<WordModeSelectorProps> = ({
  wordMode,
  customWord = '',
  categoryId = null,
  categories,
  onModeChange,
  onCustomWordChange,
  onCategoryChange,
  disabled = false
}) => {
  const { t } = useTranslation();
  return (
    <div className="mb-6">
      <div className="flex items-center mb-3">
        <label className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {t('tableSetup.wordMode.label')}
        </label>
        <InfoButton
          title={t('tableSetup.wordMode.infoTitle')}
          content={t('tableSetup.wordMode.infoContent')}
        />
      </div>

      {/* Mode Selection */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <button
          type="button"
          onClick={() => onModeChange('random')}
          disabled={disabled}
          className={`
            py-3 px-4 rounded-lg font-medium transition-all duration-200
            ${wordMode === 'random'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 border-2 border-gray-200 dark:border-gray-800'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {t('tableSetup.wordMode.random')}
        </button>

        <button
          type="button"
          onClick={() => onModeChange('custom')}
          disabled={disabled}
          className={`
            py-3 px-4 rounded-lg font-medium transition-all duration-200
            ${wordMode === 'custom'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 border-2 border-gray-200 dark:border-gray-800'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {t('tableSetup.wordMode.custom')}
        </button>

        <button
          type="button"
          onClick={() => onModeChange('category')}
          disabled={disabled}
          className={`
            py-3 px-4 rounded-lg font-medium transition-all duration-200
            ${wordMode === 'category'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 border-2 border-gray-200 dark:border-gray-800'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {t('tableSetup.wordMode.category')}
        </button>
      </div>

      {/* Custom Word Input */}
      {wordMode === 'custom' && (
        <div className="animate-fade-in">
          <input
            type="text"
            value={customWord}
            onChange={(e) => onCustomWordChange(e.target.value)}
            disabled={disabled}
            maxLength={30}
            placeholder={t('tableSetup.wordMode.customPlaceholder')}
            className={`
              w-full px-4 py-3 border-2 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent
              transition-all duration-200
              ${disabled
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 hover:border-blue-300 dark:border-gray-700'
              }
            `}
          />
        </div>
      )}

      {/* Category Selector */}
      {wordMode === 'category' && (
        <div className="animate-fade-in">
          <CategorySelector
            categories={categories}
            selectedCategoryId={categoryId}
            onSelectCategory={onCategoryChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default WordModeSelector;
